/**
 * Frustum Culling Utilities
 * Calculate which tiles are visible in the camera's view frustum
 */

import { Frustum, Matrix4, Box3, Vector3, Sphere } from 'three'

/**
 * Calculate which tiles are visible in the camera frustum
 * @param {PerspectiveCamera} camera - The camera
 * @param {Object} tileGrid - Grid configuration {columns, rows}
 * @param {number} sphereRadius - Radius of the Earth sphere
 * @returns {Set<number>} Set of visible tile indices
 */
export function getVisibleTiles(camera, tileGrid, sphereRadius = 2.5) {
  const frustum = new Frustum()
  const projectionMatrix = new Matrix4().multiplyMatrices(
    camera.projectionMatrix,
    camera.matrixWorldInverse
  )
  frustum.setFromProjectionMatrix(projectionMatrix)

  const visibleIndices = new Set()
  const { columns, rows } = tileGrid

  // Calculate tile bounds in UV space
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      const tileIndex = row * columns + col

      // Calculate UV bounds for this tile
      const uMin = col / columns
      const uMax = (col + 1) / columns
      const vMin = row / rows
      const vMax = (row + 1) / rows

      // Convert UV to spherical coordinates and check if visible
      if (isTileVisible(frustum, uMin, uMax, vMin, vMax, sphereRadius, camera)) {
        visibleIndices.add(tileIndex)
      }
    }
  }

  return visibleIndices
}

/**
 * Check if a tile (defined by UV bounds) is visible in the frustum
 * Uses bounding sphere test for performance and backface culling
 */
function isTileVisible(frustum, uMin, uMax, vMin, vMax, radius, camera) {
  // Calculate tile center for backface culling
  const centerU = (uMin + uMax) / 2
  const centerV = (vMin + vMax) / 2
  
  const centerLon = centerU * Math.PI * 2
  const centerLat = Math.PI - centerV * Math.PI
  
  const centerX = radius * Math.sin(centerLat) * Math.cos(centerLon)
  const centerY = radius * Math.cos(centerLat)
  const centerZ = radius * Math.sin(centerLat) * Math.sin(centerLon)
  
  const tileCenter = new Vector3(centerX, centerY, centerZ)
  
  // Backface culling: check if tile is facing toward camera
  // The normal at the tile center points outward from sphere center
  const normal = tileCenter.clone().normalize()
  
  // Vector from tile center to camera
  const toCamera = camera.position.clone().sub(tileCenter).normalize()
  
  // Dot product: if negative, tile is facing away from camera
  const dot = normal.dot(toCamera)
  if (dot <= 0) {
    return false // Tile is on the back side
  }
  
  // Sample points on the tile to create a bounding sphere
  const points = []
  
  // Sample corners and center
  const uvSamples = [
    [uMin, vMin],
    [uMax, vMin],
    [uMin, vMax],
    [uMax, vMax],
    [centerU, centerV]
  ]

  for (const [u, v] of uvSamples) {
    // Convert UV to spherical coordinates
    // u = longitude (0 to 1 maps to 0 to 2π)
    // v = latitude (0 to 1 maps to π to 0, top to bottom)
    const lon = u * Math.PI * 2
    const lat = Math.PI - v * Math.PI

    // Spherical to Cartesian
    const x = radius * Math.sin(lat) * Math.cos(lon)
    const y = radius * Math.cos(lat)
    const z = radius * Math.sin(lat) * Math.sin(lon)

    points.push(new Vector3(x, y, z))
  }

  // Create bounding sphere from points
  const box = new Box3()
  box.setFromPoints(points)
  
  const sphere = new Sphere()
  box.getBoundingSphere(sphere)

  // Test if bounding sphere intersects frustum
  return frustum.intersectsSphere(sphere)
}

/**
 * Get the center point of a tile in 3D space
 * Useful for distance-based LOD within visible tiles
 */
export function getTileCenter(col, row, columns, rows, radius = 2.5) {
  const u = (col + 0.5) / columns
  const v = (row + 0.5) / rows

  const lon = u * Math.PI * 2
  const lat = Math.PI - v * Math.PI

  const x = radius * Math.sin(lat) * Math.cos(lon)
  const y = radius * Math.cos(lat)
  const z = radius * Math.sin(lat) * Math.sin(lon)

  return new Vector3(x, y, z)
}
